import type { CaptureResult, PostHog } from 'posthog-js'

import { isFreeEmailDomain, normalizeCompanyGroup } from './analytics-privacy'

export { normalizeCompanyGroup } from './analytics-privacy'

export type PostHogClientLike = Pick<PostHog, 'init' | 'identify' | 'group' | 'capture' | 'people'>

type BrowserPostHogGlobal = { posthog?: PostHogClientLike }

const ATTRIBUTION_QUERY_KEYS = new Set([
  'utm_source',
  'utm_medium',
  'utm_campaign',
  'utm_term',
  'utm_content',
  'gclid',
  'fbclid',
  'li_fat_id',
])

export function sanitizeAnalyticsUrl(value: string): string {
  try {
    const url = new URL(value, 'https://forgepoint.ai')
    const allowed = new URLSearchParams()
    for (const [key, queryValue] of url.searchParams) {
      if (ATTRIBUTION_QUERY_KEYS.has(key)) allowed.append(key, queryValue)
    }
    url.search = allowed.toString()
    return url.toString()
  } catch {
    return value.split('?')[0]
  }
}

function sanitizeCaptureEvent(event: CaptureResult | null): CaptureResult | null {
  if (!event) return event
  const next = event as CaptureResult & { properties?: Record<string, unknown> }
  if (!next.properties) return event
  const properties = { ...next.properties }
  for (const key of ['$current_url', '$referrer', 'url']) {
    if (typeof properties[key] === 'string') properties[key] = sanitizeAnalyticsUrl(properties[key])
  }
  return { ...next, properties }
}

export function createPostHogBridge(options: {
  client: PostHogClientLike
  getBrowserGlobal: () => BrowserPostHogGlobal | undefined
  key: string
  host: string
  uiHost?: string
  deployEnvironment?: string
  isDevelopment: boolean
}) {
  let initializedClient: PostHogClientLike | null = null

  const getPostHog = (): PostHogClientLike | null => (
    options.getBrowserGlobal()?.posthog || initializedClient
  )

  const initPostHog = (): PostHogClientLike | null => {
    const browserGlobal = options.getBrowserGlobal()
    if (!browserGlobal) return null
    if (browserGlobal.posthog) return browserGlobal.posthog
    if (initializedClient) return initializedClient
    if (!options.key) return null

    options.client.init(options.key, {
      api_host: options.host,
      ui_host: options.uiHost,
      defaults: '2026-05-30',
      loaded: (loadedClient) => {
        if (options.isDevelopment) loadedClient.debug()
      },
      autocapture: true,
      capture_pageview: 'history_change',
      capture_pageleave: true,
      persistence: 'localStorage+cookie',
      disable_session_recording: options.deployEnvironment !== 'production',
      session_recording: {
        maskAllInputs: true,
        maskTextSelector: '.ph-mask, .ph-no-capture',
        blockClass: 'ph-no-capture',
        recordCrossOriginIframes: false,
        recordHeaders: false,
        recordBody: false,
      },
      enable_recording_console_log: false,
      cross_subdomain_cookie: true,
      capture_exceptions: true,
      capture_performance: true,
      before_send: sanitizeCaptureEvent,
    })
    initializedClient = options.client
    return initializedClient
  }

  const identifyFromEmail = (email: string, properties?: Record<string, any>): void => {
    const client = getPostHog()
    if (!client || !email) return

    const cleanedEmail = email.toLowerCase().trim()
    const domain = cleanedEmail.split('@')[1] || ''
    const company = typeof properties?.company === 'string' ? properties.company : undefined
    client.identify(cleanedEmail, {
      email: cleanedEmail,
      email_domain: domain,
      ...properties,
    })
    const groupKey = normalizeCompanyGroup(cleanedEmail, company)
    if (groupKey) {
      client.group('company', groupKey, domain && !isFreeEmailDomain(domain)
        ? { domain }
        : { company_name: company })
    }
  }

  return {
    initPostHog,
    getPostHog,
    identifyFromEmail,
    setUserProperties: (properties: Record<string, any>): void => {
      getPostHog()?.people.set(properties)
    },
    trackEvent: (eventName: string, properties?: Record<string, any>): void => {
      getPostHog()?.capture(eventName, properties)
    },
  }
}

type PostHogBridge = ReturnType<typeof createPostHogBridge>

const browserKey = process.env.NEXT_PUBLIC_POSTHOG_PROJECT_TOKEN
  || process.env.NEXT_PUBLIC_POSTHOG_KEY
  || ''
let postHogBridge: PostHogBridge | null = null
let postHogBridgePromise: Promise<PostHogBridge | null> | null = null
let mobileAnalyticsActivated = false
let mobileAnalyticsArmed = false
let pendingMobileActions: Array<(client: PostHog) => void> = []

function activateMobileAnalytics(): void {
  if (mobileAnalyticsActivated) return
  mobileAnalyticsActivated = true
  void ensurePostHogBridge().then((bridge) => {
    const client = bridge?.getPostHog() as PostHog | null
    if (!client) return
    const queued = pendingMobileActions
    pendingMobileActions = []
    queued.forEach((action) => action(client))
  })
}

function deferMobileAnalytics(action: (client: PostHog) => void): boolean {
  if (
    typeof window === 'undefined'
    || !window.matchMedia('(max-width: 700px)').matches
    || mobileAnalyticsActivated
  ) return false

  pendingMobileActions.push(action)
  if (!mobileAnalyticsArmed) {
    mobileAnalyticsArmed = true
    const activate = () => activateMobileAnalytics()
    window.addEventListener('pointerdown', activate, { once: true, passive: true })
    window.addEventListener('keydown', activate, { once: true })
    window.addEventListener('scroll', activate, { once: true, passive: true })
    window.setTimeout(activate, 10000)
  }
  return true
}

async function ensurePostHogBridge(): Promise<PostHogBridge | null> {
  if (postHogBridge) return postHogBridge
  if (postHogBridgePromise) return postHogBridgePromise
  if (typeof window === 'undefined' || !browserKey) return null

  postHogBridgePromise = import('posthog-js').then(({ default: client }) => {
    const bridge = createPostHogBridge({
      client,
      getBrowserGlobal: () => window as typeof window & BrowserPostHogGlobal,
      key: browserKey,
      host: '/ingest',
      uiHost: process.env.NEXT_PUBLIC_POSTHOG_UI_HOST || 'https://us.posthog.com',
      deployEnvironment: process.env.NEXT_PUBLIC_DEPLOY_ENV || process.env.VERCEL_ENV || 'local',
      isDevelopment: process.env.NODE_ENV === 'development',
    })
    bridge.initPostHog()
    postHogBridge = bridge
    return bridge
  }).catch((error) => {
    if (process.env.NODE_ENV === 'development') console.warn('PostHog failed to initialize', error)
    return null
  })

  return postHogBridgePromise
}

function withPostHog(action: (client: PostHog) => void): void {
  const loaded = getPostHog()
  if (loaded) {
    action(loaded)
    return
  }
  if (deferMobileAnalytics(action)) return
  void ensurePostHogBridge().then((bridge) => {
    const client = bridge?.getPostHog() as PostHog | null
    if (client) action(client)
  })
}

export const initPostHog = async (): Promise<PostHog | null> => {
  const bridge = await ensurePostHogBridge()
  return bridge?.getPostHog() as PostHog | null
}

// Get the PostHog instance
export const getPostHog = (): PostHog | null => {
  return postHogBridge?.getPostHog() as PostHog | null;
};

// Wrapper function to track events safely
export const trackEvent = (eventName: string, properties?: Record<string, any>): void => {
  withPostHog((client) => client.capture(eventName, properties));
};

// Identify a user by email. Also groups by company (email domain).
// Idempotent — safe to call on every form submit. PostHog will merge
// any prior anonymous session into this identified person.
export const identifyFromEmail = (
  email: string,
  properties?: Record<string, any>
): void => {
  if (postHogBridge) {
    postHogBridge.identifyFromEmail(email, properties)
    return
  }
  void ensurePostHogBridge().then((bridge) => bridge?.identifyFromEmail(email, properties))
};

// Identify a user by explicit ID (e.g. internal user ID after auth).
export const identifyUser = (userId: string, properties?: Record<string, any>): void => {
  withPostHog((client) => client.identify(userId, properties));
};

export const captureClientException = (
  error: unknown,
  properties?: Record<string, unknown>,
): void => {
  withPostHog((client) => client.captureException(error, properties))
}

// Set user properties without identifying
export const setUserProperties = (properties: Record<string, any>): void => {
  withPostHog((client) => client.people.set(properties));
};

// Register super properties — attached to every subsequent event in this session.
export const registerSuperProperties = (properties: Record<string, any>): void => {
  withPostHog((client) => client.register(properties));
};

// Register super properties exactly once per browser (for first-touch attribution).
export const registerSuperPropertiesOnce = (properties: Record<string, any>): void => {
  withPostHog((client) => client.register_once(properties));
};

// Track page views manually (if you disable automatic tracking)
export const trackPageView = (pageName?: string, properties?: Record<string, any>): void => {
  withPostHog((client) => {
    client.capture('$pageview', {
      $current_url: window.location.href,
      $host: window.location.host,
      $pathname: window.location.pathname,
      ...(pageName && { page_name: pageName }),
      ...properties,
    });
  })
};

// Reset user (useful for logout)
export const resetUser = (): void => {
  withPostHog((client) => client.reset());
};

// Opt user out of tracking
export const optOut = (): void => {
  withPostHog((client) => client.opt_out_capturing());
};

// Opt user back in to tracking
export const optIn = (): void => {
  withPostHog((client) => client.opt_in_capturing());
};

export const getPostHogTracingHeaders = (): Record<string, string> => {
  const ph = getPostHog()
  if (!ph) return {}
  return {
    'x-posthog-distinct-id': ph.get_distinct_id(),
    'x-posthog-session-id': ph.get_session_id(),
  }
}
